module.exports = {
	"data": {
		"markdownRemark": {
			"html": "<h3>Motivation</h3>\n<p>Scala is a very popular language nowadays and it’s often chosen to deliver efficient and scalable systems. It leverages the Java VM, known for its reliability and robustness. Support for Functional Programming, rich ecosystem and stable foundation allow building fast applications, quickly.</p>\n<p>In the next chapters you’ll learn how to build your own GraphQL server using Scala and the following technologies:</p>\n<ul>\n<li><a href=\"https://www.scala-lang.org/\">Scala</a> Scala language</li>\n<li><a href=\"http://doc.akka.io/docs/akka-http/current/scala/http\">Akka HTTP</a> Web server to handle HTTP requests.</li>\n<li><a href=\"http://sangria-graphql.org/\">Sangria</a> A library for GraphQL execution</li>\n<li><a href=\"http://slick.lightbend.com/\">Slick</a> A Database query and access library.</li>\n<li><a href=\"http://www.h2database.com/html/main.html\">H2 Database</a> In-memory database.</li>\n<li><a href=\"https://github.com/graphql/graphiql\">Graphiql</a> A simple GraphQL console to play with.</li>\n<li><a href=\"https://github.com/foundweekends/giter8\">Giter8</a> A project templating tool for Scala.</li>\n</ul>\n<p>I assume you’re familiar with GraphQL concepts, but if not, you can visit <a href=\"http://graphql.org/\">GraphQL site</a> to learn more about that.</p>\n<h3>What is a GraphQL Server?</h3>\n<p>A GraphQL server should be able to:</p>\n<ul>\n<li>Receive requests following the GraphQL format, for example:</li>\n</ul>\n<div class=\"gatsby-highlight\">\n      <pre class=\"language-graphql\"   nocopy=\"true\"><code><span class=\"token punctuation\">{</span>  <span class=\"token string\">\"query\"</span><span class=\"token punctuation\">:</span> <span class=\"token string\">\"query { allLinks { url } }\"</span> <span class=\"token punctuation\">}</span>\n</code></pre>\n      </div>\n<ul>\n<li>Connect to one or more data sources, like databases or other APIs and format obtained information.</li>\n<li>Response with the requested data, such as this one:</li>\n</ul>\n<div class=\"gatsby-highlight\">\n      <pre class=\"language-graphql\"   nocopy=\"true\"><code><span class=\"token punctuation\">{</span> <span class=\"token string\">\"data\"</span><span class=\"token punctuation\">:</span> <span class=\"token punctuation\">{</span> <span class=\"token string\">\"allLinks\"</span><span class=\"token punctuation\">:</span> <span class=\"token punctuation\">{</span> <span class=\"token string\">\"url\"</span><span class=\"token punctuation\">:</span> <span class=\"token string\">\"http://graphql.org/\"</span> <span class=\"token punctuation\">}</span> <span class=\"token punctuation\">}</span> <span class=\"token punctuation\">}</span>\n</code></pre>\n      </div>\n<ul>\n<li>Validate incoming requests accordingly to the schema definition and supported formats. For example, if a query is made with an unknown field, the response should be something like:</li>\n</ul>\n<div class=\"gatsby-highlight\">\n      <pre class=\"language-graphql\"   nocopy=\"true\"><code><span class=\"token punctuation\">{</span>\n  <span class=\"token string\">\"errors\"</span><span class=\"token punctuation\">:</span> <span class=\"token punctuation\">[</span><span class=\"token punctuation\">{</span>\n    <span class=\"token string\">\"message\"</span><span class=\"token punctuation\">:</span> <span class=\"token string\">\"Cannot query field \\\"unknown\\\" on type \\\"Link\\\".\"</span>\n  <span class=\"token punctuation\">}</span><span class=\"token punctuation\">]</span>\n<span class=\"token punctuation\">}</span>\n</code></pre>\n      </div>\n<p>As you can see our server will be really simple, but real GraphQL implementation can do much more than this. (We will explore it more later on.)</p>\n<h3>Schema-Driven Development</h3>\n<p>Schema-first GraphQL development forces frontend and backend developers to agree on a strict contract up front, enabling them to work quickly and efficiently while staying on spec. It improves both your API’s performance and the performance of your team in general.</p>\n<p>Sensibly then, the experience of building a GraphQL server starts with working on its schema. You’ll see in this chapter that the main steps you follow will be something like this:</p>\n<ol>\n<li>Define your types and the appropriate queries and mutations for them.</li>\n<li>Implement functions (called <strong>resolvers</strong>) to perform agreed upon queries in terms of defined types.</li>\n<li>As new requirements arrive, go back to step 1 to update the schema and go through the other steps.</li>\n</ol>\n<p>The schema is a contract between the frontend and backend, so keeping it at the center allows both sides of the development to evolve without going off the spec. This also makes it easier to parallelize the work. Since the frontend can move on with full knowledge of the API from the start, using a simple mocking service (or even a full backend such as Graphcool) which can later be easily replaced with the final server.</p>\n<h3>Goal of the tutorial</h3>\n<p>Most of the HowToGraphQL tutorials are based on the same schema. In our tutorial we will try to run scala server which supports that schema. In this case you can take any frontend example and connect it to our server.\nThe schema more or less looks like this:</p>\n<div class=\"gatsby-highlight\">\n      <pre class=\"language-graphql\"   nocopy=\"false\"><code>type Query <span class=\"token punctuation\">{</span>\n  allLinks<span class=\"token punctuation\">(</span><span class=\"token attr-name\">filter</span><span class=\"token punctuation\">:</span> LinkFilter<span class=\"token punctuation\">,</span> <span class=\"token attr-name\">orderBy</span><span class=\"token punctuation\">:</span> LinkOrderBy<span class=\"token punctuation\">,</span> <span class=\"token attr-name\">skip</span><span class=\"token punctuation\">:</span> Int<span class=\"token punctuation\">,</span> <span class=\"token attr-name\">first</span><span class=\"token punctuation\">:</span> Int<span class=\"token punctuation\">)</span><span class=\"token punctuation\">:</span> <span class=\"token punctuation\">[</span>Link<span class=\"token operator\">!</span><span class=\"token punctuation\">]</span><span class=\"token operator\">!</span>\n  <span class=\"token attr-name\">_allLinksMeta</span><span class=\"token punctuation\">:</span> _QueryMeta<span class=\"token operator\">!</span>\n<span class=\"token punctuation\">}</span>\n\ntype Mutation <span class=\"token punctuation\">{</span>\n  signinUser<span class=\"token punctuation\">(</span><span class=\"token attr-name\">email</span><span class=\"token punctuation\">:</span> AUTH_PROVIDER_EMAIL<span class=\"token punctuation\">)</span><span class=\"token punctuation\">:</span> SigninPayload<span class=\"token operator\">!</span>\n  createUser<span class=\"token punctuation\">(</span><span class=\"token attr-name\">name</span><span class=\"token punctuation\">:</span> String<span class=\"token operator\">!</span><span class=\"token punctuation\">,</span> <span class=\"token attr-name\">authProvider</span><span class=\"token punctuation\">:</span> AuthProviderSignupData<span class=\"token operator\">!</span><span class=\"token punctuation\">)</span><span class=\"token punctuation\">:</span> User\n  createLink<span class=\"token punctuation\">(</span><span class=\"token attr-name\">description</span><span class=\"token punctuation\">:</span> String<span class=\"token operator\">!</span><span class=\"token punctuation\">,</span> <span class=\"token attr-name\">url</span><span class=\"token punctuation\">:</span> String<span class=\"token operator\">!</span><span class=\"token punctuation\">,</span> <span class=\"token attr-name\">postedById</span><span class=\"token punctuation\">:</span> ID<span class=\"token punctuation\">)</span><span class=\"token punctuation\">:</span> Link\n  createVote<span class=\"token punctuation\">(</span><span class=\"token attr-name\">linkId</span><span class=\"token punctuation\">:</span> ID<span class=\"token punctuation\">,</span> <span class=\"token attr-name\">userId</span><span class=\"token punctuation\">:</span> ID<span class=\"token punctuation\">)</span><span class=\"token punctuation\">:</span> Vote\n<span class=\"token punctuation\">}</span>\n\ntype Subscription <span class=\"token punctuation\">{</span>\n  Link<span class=\"token punctuation\">(</span><span class=\"token attr-name\">filter</span><span class=\"token punctuation\">:</span> LinkSubscriptionFilter<span class=\"token punctuation\">)</span><span class=\"token punctuation\">:</span> LinkSubscriptionPayload\n  Vote<span class=\"token punctuation\">(</span><span class=\"token attr-name\">filter</span><span class=\"token punctuation\">:</span> VoteSubscriptionFilter<span class=\"token punctuation\">)</span><span class=\"token punctuation\">:</span> VoteSubscriptionPayload\n<span class=\"token punctuation\">}</span>\n\ninterface Node <span class=\"token punctuation\">{</span>\n  <span class=\"token attr-name\">id</span><span class=\"token punctuation\">:</span> ID<span class=\"token operator\">!</span>\n<span class=\"token punctuation\">}</span>\n\ntype User implements Node <span class=\"token punctuation\">{</span>\n  <span class=\"token attr-name\">id</span><span class=\"token punctuation\">:</span> ID<span class=\"token operator\">!</span> <span class=\"token directive function\">@isUnique</span>\n  <span class=\"token attr-name\">createdAt</span><span class=\"token punctuation\">:</span> DateTime<span class=\"token operator\">!</span>\n  <span class=\"token attr-name\">name</span><span class=\"token punctuation\">:</span> String<span class=\"token operator\">!</span>\n  <span class=\"token attr-name\">links</span><span class=\"token punctuation\">:</span> <span class=\"token punctuation\">[</span>Link<span class=\"token operator\">!</span><span class=\"token punctuation\">]</span><span class=\"token operator\">!</span> <span class=\"token directive function\">@relation</span><span class=\"token punctuation\">(</span><span class=\"token attr-name\">name</span><span class=\"token punctuation\">:</span> <span class=\"token string\">\"UsersLinks\"</span><span class=\"token punctuation\">)</span>\n  <span class=\"token attr-name\">votes</span><span class=\"token punctuation\">:</span> <span class=\"token punctuation\">[</span>Vote<span class=\"token operator\">!</span><span class=\"token punctuation\">]</span><span class=\"token operator\">!</span> <span class=\"token directive function\">@relation</span><span class=\"token punctuation\">(</span><span class=\"token attr-name\">name</span><span class=\"token punctuation\">:</span> <span class=\"token string\">\"UsersVotes\"</span><span class=\"token punctuation\">)</span>\n  <span class=\"token attr-name\">email</span><span class=\"token punctuation\">:</span> String <span class=\"token directive function\">@isUnique</span>\n  <span class=\"token attr-name\">password</span><span class=\"token punctuation\">:</span> String\n<span class=\"token punctuation\">}</span>\n\ntype Link implements Node <span class=\"token punctuation\">{</span>\n  <span class=\"token attr-name\">id</span><span class=\"token punctuation\">:</span> ID<span class=\"token operator\">!</span> <span class=\"token directive function\">@isUnique</span>\n  <span class=\"token attr-name\">createdAt</span><span class=\"token punctuation\">:</span> DateTime<span class=\"token operator\">!</span>\n  <span class=\"token attr-name\">url</span><span class=\"token punctuation\">:</span> String<span class=\"token operator\">!</span>\n  <span class=\"token attr-name\">description</span><span class=\"token punctuation\">:</span> String<span class=\"token operator\">!</span>\n  <span class=\"token attr-name\">postedBy</span><span class=\"token punctuation\">:</span> User<span class=\"token operator\">!</span> <span class=\"token directive function\">@relation</span><span class=\"token punctuation\">(</span><span class=\"token attr-name\">name</span><span class=\"token punctuation\">:</span> <span class=\"token string\">\"UsersLinks\"</span><span class=\"token punctuation\">)</span>\n  <span class=\"token attr-name\">votes</span><span class=\"token punctuation\">:</span> <span class=\"token punctuation\">[</span>Vote<span class=\"token operator\">!</span><span class=\"token punctuation\">]</span><span class=\"token operator\">!</span> <span class=\"token directive function\">@relation</span><span class=\"token punctuation\">(</span><span class=\"token attr-name\">name</span><span class=\"token punctuation\">:</span> <span class=\"token string\">\"VotesOnLink\"</span><span class=\"token punctuation\">)</span>\n<span class=\"token punctuation\">}</span>\n\ntype Vote implements Node <span class=\"token punctuation\">{</span>\n  <span class=\"token attr-name\">id</span><span class=\"token punctuation\">:</span> ID<span class=\"token operator\">!</span> <span class=\"token directive function\">@isUnique</span>\n  <span class=\"token attr-name\">createdAt</span><span class=\"token punctuation\">:</span> DateTime<span class=\"token operator\">!</span>\n  <span class=\"token attr-name\">user</span><span class=\"token punctuation\">:</span> User<span class=\"token operator\">!</span> <span class=\"token directive function\">@relation</span><span class=\"token punctuation\">(</span><span class=\"token attr-name\">name</span><span class=\"token punctuation\">:</span> <span class=\"token string\">\"UsersVotes\"</span><span class=\"token punctuation\">)</span>\n  <span class=\"token attr-name\">link</span><span class=\"token punctuation\">:</span> Link<span class=\"token operator\">!</span> <span class=\"token directive function\">@relation</span><span class=\"token punctuation\">(</span><span class=\"token attr-name\">name</span><span class=\"token punctuation\">:</span> <span class=\"token string\">\"VotesOnLink\"</span><span class=\"token punctuation\">)</span>\n<span class=\"token punctuation\">}</span>\n\ninput AuthProviderSignupData <span class=\"token punctuation\">{</span>\n  <span class=\"token attr-name\">email</span><span class=\"token punctuation\">:</span> AUTH_PROVIDER_EMAIL\n<span class=\"token punctuation\">}</span>\n\ninput AUTH_PROVIDER_EMAIL <span class=\"token punctuation\">{</span>\n  <span class=\"token attr-name\">email</span><span class=\"token punctuation\">:</span> String<span class=\"token operator\">!</span>\n  <span class=\"token attr-name\">password</span><span class=\"token punctuation\">:</span> String<span class=\"token operator\">!</span>\n<span class=\"token punctuation\">}</span>\n\ninput LinkSubscriptionFilter <span class=\"token punctuation\">{</span>\n  <span class=\"token attr-name\">mutation_in</span><span class=\"token punctuation\">:</span> <span class=\"token punctuation\">[</span>_ModelMutationType<span class=\"token operator\">!</span><span class=\"token punctuation\">]</span>\n<span class=\"token punctuation\">}</span>\n\ninput VoteSubscriptionFilter <span class=\"token punctuation\">{</span>\n  <span class=\"token attr-name\">mutation_in</span><span class=\"token punctuation\">:</span> <span class=\"token punctuation\">[</span>_ModelMutationType<span class=\"token operator\">!</span><span class=\"token punctuation\">]</span>\n<span class=\"token punctuation\">}</span>\n\ninput LinkFilter <span class=\"token punctuation\">{</span>\n  <span class=\"token attr-name\">OR</span><span class=\"token punctuation\">:</span> <span class=\"token punctuation\">[</span>LinkFilter<span class=\"token operator\">!</span><span class=\"token punctuation\">]</span>\n  <span class=\"token attr-name\">description_contains</span><span class=\"token punctuation\">:</span> String\n  <span class=\"token attr-name\">url_contains</span><span class=\"token punctuation\">:</span> String\n<span class=\"token punctuation\">}</span>\n\ntype SigninPayload <span class=\"token punctuation\">{</span>\n  <span class=\"token attr-name\">token</span><span class=\"token punctuation\">:</span> String\n  <span class=\"token attr-name\">user</span><span class=\"token punctuation\">:</span> User\n<span class=\"token punctuation\">}</span>\n\ntype LinkSubscriptionPayload <span class=\"token punctuation\">{</span>\n  <span class=\"token attr-name\">mutation</span><span class=\"token punctuation\">:</span> _ModelMutationType<span class=\"token operator\">!</span>\n  <span class=\"token attr-name\">node</span><span class=\"token punctuation\">:</span> Link\n  <span class=\"token attr-name\">updatedFields</span><span class=\"token punctuation\">:</span> <span class=\"token punctuation\">[</span>String<span class=\"token operator\">!</span><span class=\"token punctuation\">]</span>\n<span class=\"token punctuation\">}</span>\n\ntype VoteSubscriptionPayload <span class=\"token punctuation\">{</span>\n  <span class=\"token attr-name\">mutation</span><span class=\"token punctuation\">:</span> _ModelMutationType<span class=\"token operator\">!</span>\n  <span class=\"token attr-name\">node</span><span class=\"token punctuation\">:</span> Vote\n  <span class=\"token attr-name\">updatedFields</span><span class=\"token punctuation\">:</span> <span class=\"token punctuation\">[</span>String<span class=\"token operator\">!</span><span class=\"token punctuation\">]</span>\n<span class=\"token punctuation\">}</span>\n\nenum LinkOrderBy <span class=\"token punctuation\">{</span>\n  createdAt_ASC\n  createdAt_DESC\n<span class=\"token punctuation\">}</span>\n\nenum _ModelMutationType <span class=\"token punctuation\">{</span>\n  CREATED\n  UPDATED\n  DELETED\n<span class=\"token punctuation\">}</span>\n\ntype _QueryMeta <span class=\"token punctuation\">{</span>\n  <span class=\"token attr-name\">count</span><span class=\"token punctuation\">:</span> Int<span class=\"token operator\">!</span>\n<span class=\"token punctuation\">}</span>\n\nscalar DateTime\n</code></pre>\n      </div>\n<p>When we know what to do, we move on to the next chapter and begin the tutorial.</p>",
			"fields": {
				"slug": "/graphql-scala/0-introduction/"
			},
			"excerpt": "Motivation Scala is a very popular language nowadays and it’s often chosen to deliver efficient and scalable systems. It leverages the Java...",
			"frontmatter": {
				"title": "Introduction",
				"videoId": null,
				"videoAuthor": null,
				"parent": "/opt/build/repo/content/backend/graphql-scala/0-introduction.md absPath of file",
				"question": "What is a GraphQL?",
				"answers": [
					"Protocol",
					"Library",
					"Language",
					"Specification"
				],
				"correctAnswer": 3,
				"description": "Learn how to build a GraphQL server with Scala & Sangria and the best practices for filters, authentication and pagination.",
				"pageTitle": "Building a GraphQL Server with Scala Backend Tutorial"
			}
		},
		"mds": {
			"edges": [
				{
					"node": {
						"frontmatter": {
							"title": "Get started with your favorite technology",
							"description": null
						},
						"fields": {
							"slug": "/choose/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "It was great having you!",
							"description": null
						},
						"fields": {
							"slug": "/success/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Introduction",
							"description": "Learn how to build a GraphQL server with Elixir & Absinthe and best practices for filters, authentication, pagination and subscriptions. Compatible with Apollo."
						},
						"fields": {
							"slug": "/graphql-elixir/0-introduction/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Queries",
							"description": "Learn how to define the GraphQL schema with Absinthe, implement query resolvers in Elixir and test your queries in a GraphiQL Playground."
						},
						"fields": {
							"slug": "/graphql-elixir/2-queries/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Getting Started",
							"description": "Learn how to setup a GraphQL server with Elixir & Absinthe as well as best practices for defining the GraphQL schema."
						},
						"fields": {
							"slug": "/graphql-elixir/1-getting-started/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Summary",
							"description": "You learned how to build a GraphQL server with Elixir & Absinthe and best practices for filters, authentication, pagination and subscriptions."
						},
						"fields": {
							"slug": "/graphql-elixir/4-summary/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Mutations",
							"description": "Learn best practices for implementing GraphQL mutations with Absinthe & Elixir. You can test your implementation in a GraphiQL Playground."
						},
						"fields": {
							"slug": "/graphql-elixir/3-mutations/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Introduction",
							"description": "Learn how to build a GraphQL server with graphql-java and best practices for filters, authentication, pagination and subscriptions. Compatible with Apollo."
						},
						"fields": {
							"slug": "/graphql-java/0-introduction/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Pagination",
							"description": "Learn best practices for implementing limit-offset pagination in a GraphQL API using query arguments with a Node.js & Express GraphQL server."
						},
						"fields": {
							"slug": "/graphql-java/10-pagination/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Alternative approaches to schema development",
							"description": "Learn about an alternative approach to developing schemas with graphl-java with graphql-spqr"
						},
						"fields": {
							"slug": "/graphql-java/11-alternative-approaches/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Getting Started",
							"description": "Learn how to setup a GraphQL server with graphql-java and best practices for defining the GraphQL schema in this tutorial."
						},
						"fields": {
							"slug": "/graphql-java/1-getting-started/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Summary",
							"description": "You learned how to build a GraphQL server with graphql-java and best practices for filters, authentication, pagination and subscriptions."
						},
						"fields": {
							"slug": "/graphql-java/12-summary/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Mutations",
							"description": "Learn best practices for implementing GraphQL mutations with graphql-java & Java. You can test your implementation in a GraphiQL Playground."
						},
						"fields": {
							"slug": "/graphql-java/3-mutations/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Connectors",
							"description": "Learn how to write connectors in Java to load data from MongoDB with graphql-java and a Java GraphQL server."
						},
						"fields": {
							"slug": "/graphql-java/4-connectors/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Queries",
							"description": "Learn how to define the GraphQL schema with graphql-java, implement query resolvers in Java and test your queries in a GraphiQL Playground."
						},
						"fields": {
							"slug": "/graphql-java/2-queries/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Authentication",
							"description": "Learn best practices for implementing email-password authentication on a GraphQL Server written in Java."
						},
						"fields": {
							"slug": "/graphql-java/5-authentication/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "More Mutations",
							"description": "Learn best practices for implementing advanced GraphQL mutations with Java and graphql-java. You can test your implementation in a GraphiQL Playground."
						},
						"fields": {
							"slug": "/graphql-java/6-more-mutations/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Error Handling",
							"description": "Learn best practices for validation of input arguments and implementing error handling in GraphQL with Java, Node.js & Express."
						},
						"fields": {
							"slug": "/graphql-java/7-error-handling/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Subscriptions",
							"description": "Learn how to implement server-side GraphQL subscriptions with Java to add realtime functionality to an app."
						},
						"fields": {
							"slug": "/graphql-java/8-subscriptions/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Filtering",
							"description": "Learn best practices for implementing filters in a GraphQL API using query arguments with a Java GraphQL server."
						},
						"fields": {
							"slug": "/graphql-java/9-filtering/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Introduction",
							"description": "Learn how to build a GraphQL server with graphql-yoga, Node.js & Prisma and best practices for authentication, filtering, pagination and subscriptions."
						},
						"fields": {
							"slug": "/graphql-js/0-introduction/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Getting Started",
							"description": "Learn how to setup a GraphQL server with Node.js & Express as well as best practices for defining the GraphQL schema."
						},
						"fields": {
							"slug": "/graphql-js/1-getting-started/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "A Simple Query",
							"description": "Learn how to define a GraphQL schema, implement query resolvers with Node.js and test your queries in a GraphQL Playground."
						},
						"fields": {
							"slug": "/graphql-js/2-a-simple-query/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Adding a Database",
							"description": "Learn how to add a database to your GraphQL server. The database is powered by Prisma and connected to the server via GraphQL bindings."
						},
						"fields": {
							"slug": "/graphql-js/4-adding-a-database/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Connecting Server and Database with the Prisma Client",
							"description": "Learn how to add a database to your GraphQL server. The database is powered by Prisma and connected to the server via GraphQL bindings."
						},
						"fields": {
							"slug": "/graphql-js/5-connecting-server-and-database/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Authentication",
							"description": "Learn best practices for implementing authentication and authorization with Node.js, Express & Prisma."
						},
						"fields": {
							"slug": "/graphql-js/6-authentication/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Realtime GraphQL Subscriptions",
							"description": "Learn how to implement GraphQL subscriptions with Node.js, Express & Prisma to add realtime functionality to an app."
						},
						"fields": {
							"slug": "/graphql-js/7-subscriptions/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Filtering, Pagination & Sorting",
							"description": "Learn how to add filtering and pagination capabilities to a GraphQL API with Node.js, Express & Prisma."
						},
						"fields": {
							"slug": "/graphql-js/8-filtering-pagination-and-sorting/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "A Simple Mutation",
							"description": "Learn best practices for implementing GraphQL mutations with graphql-js, Node.js & Prisma. Test your implementation in a GraphQL Playground."
						},
						"fields": {
							"slug": "/graphql-js/3-a-simple-mutation/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Summary",
							"description": "You learned how to build a GraphQL server with Node.js and best practices for filters, authentication, pagination and subscriptions."
						},
						"fields": {
							"slug": "/graphql-js/9-summary/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Introduction",
							"description": "Learn how to build a GraphQL server with Scala & Sangria and the best practices for filters, authentication and pagination."
						},
						"fields": {
							"slug": "/graphql-scala/0-introduction/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Getting Started",
							"description": "In this chapter will be described how to setup the HTTP server, install all dependencies and setup the database."
						},
						"fields": {
							"slug": "/graphql-scala/1-getting-started/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "The first query",
							"description": "In this chapter you will learn last steps of preparation, add graphiql console for easy debugging and at the end, run your first query."
						},
						"fields": {
							"slug": "/graphql-scala/2-preparing-first-query/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Handling Arguments",
							"description": "In this chapter you will learn how to use arguments and how to handle them and pass to the business logic."
						},
						"fields": {
							"slug": "/graphql-scala/3-handling_arguments/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Deferred Resolvers",
							"description": "In this chapter you will learn how to improve performance with deferred resolvers."
						},
						"fields": {
							"slug": "/graphql-scala/4-deferred_resolvers/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Custom Scalars",
							"description": "In this chapter Link model will get an additional field to store date and time. You will learn how to handle such custom types and use scalars."
						},
						"fields": {
							"slug": "/graphql-scala/5-custom_scalars/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Interfaces",
							"description": "In this chapter we will introduce two additional models. We show the most common parts and extract those as an interface. This time you will work alone."
						},
						"fields": {
							"slug": "/graphql-scala/6-interfaces/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Relations",
							"description": "In this chapter you will learn how to connect models with relations. You will also learn how to prepare fetchers to use either relations or ids to get entities."
						},
						"fields": {
							"slug": "/graphql-scala/7-relations/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Mutations",
							"description": "In this chapter you will learn how to add data to the database."
						},
						"fields": {
							"slug": "/graphql-scala/8-mutations/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Authentication",
							"description": "Sangria's Middleware allows pre-processing queries. Gather all useful data in the following subqueries."
						},
						"fields": {
							"slug": "/graphql-scala/9-authentication/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Getting Started",
							"description": "Learn how to setup a GraphQL server with graphql-ruby and best practices for defining the GraphQL schema."
						},
						"fields": {
							"slug": "/graphql-ruby/1-getting-started/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Introduction",
							"description": "Learn how to build a GraphQL server with graphql-ruby and best practices for filters, authentication, pagination and subscriptions. Compatible with Apollo."
						},
						"fields": {
							"slug": "/graphql-ruby/0-introduction/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Queries",
							"description": "Learn how to define the GraphQL schema with graphql-ruby, implement query resolvers in Ruby and use a GraphiQL Playground to test your queries."
						},
						"fields": {
							"slug": "/graphql-ruby/2-queries/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Mutations",
							"description": "Learn best practices for implementing GraphQL mutations with Ruby and graphql-ruby. You can test your implementation in a GraphiQL Playground."
						},
						"fields": {
							"slug": "/graphql-ruby/3-mutations/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Authentication",
							"description": "Learn best practices for implementing email-password authentication on a GraphQL Server written in Ruby."
						},
						"fields": {
							"slug": "/graphql-ruby/4-authentication/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Connecting Nodes",
							"description": "Learn best practices for implementing advanced GraphQL mutations with Ruby and graphql-ruby. You can test your implementation in a GraphiQL Playground."
						},
						"fields": {
							"slug": "/graphql-ruby/5-connecting-nodes/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Error Handling",
							"description": "Learn best practices for validation of input arguments and implementing error handling in GraphQL with Ruby."
						},
						"fields": {
							"slug": "/graphql-ruby/6-error-handling/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Pagination",
							"description": "Learn best practices for implementing limit-offset pagination in a GraphQL API using query arguments with Ruby GraphQL server."
						},
						"fields": {
							"slug": "/graphql-ruby/8-pagination/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Summary",
							"description": "You learned how to build a GraphQL server with graphql-ruby & Ruby and best practices for filters, authentication, pagination and subscriptions."
						},
						"fields": {
							"slug": "/graphql-ruby/9-summary/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Introduction",
							"description": "Building a Server with GraphQL and Graphene"
						},
						"fields": {
							"slug": "/graphql-python/0-introduction/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Filtering",
							"description": "Learn best practices for implementing filters in a GraphQL API using query arguments with a Ruby GraphQL server."
						},
						"fields": {
							"slug": "/graphql-ruby/7-filtering/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Getting Started",
							"description": "Setting up a Django Graphene Project"
						},
						"fields": {
							"slug": "/graphql-python/1-getting-started/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Summary",
							"description": "Tutorial Summary"
						},
						"fields": {
							"slug": "/graphql-python/10-summary/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Queries",
							"description": "Creating Your First Queries"
						},
						"fields": {
							"slug": "/graphql-python/2-queries/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Mutations",
							"description": "Creating Data With Mutations"
						},
						"fields": {
							"slug": "/graphql-python/3-mutations/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Links and Voting",
							"description": "Enable Users to create Links and to Vote on them"
						},
						"fields": {
							"slug": "/graphql-python/5-links-and-voting/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Error Handling",
							"description": "Handling Errors on GraphQL"
						},
						"fields": {
							"slug": "/graphql-python/6-error-handling/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Authentication",
							"description": "Authenticating on GraphQL"
						},
						"fields": {
							"slug": "/graphql-python/4-authentication/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Filtering",
							"description": "Filtering GraphQL Queries"
						},
						"fields": {
							"slug": "/graphql-python/7-filtering/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Pagination",
							"description": "Simple Pagination with Graphene"
						},
						"fields": {
							"slug": "/graphql-python/8-pagination/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Relay",
							"description": "Using Relay on Graphene"
						},
						"fields": {
							"slug": "/graphql-python/9-relay/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Introduction",
							"description": "Learn how to build a Hackernews clone with GraphQL, Ember, and Apollo Client. You'll use Ember and ember-apollo-client for the frontend and Graphcool for the backend."
						},
						"fields": {
							"slug": "/ember-apollo/0-introduction/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Getting Started",
							"description": "Start building a Hackernews clone. Create the frontend with ember-cli, ember-apollo-client, and the backend with Graphcool."
						},
						"fields": {
							"slug": "/ember-apollo/1-getting-started/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Queries: Loading Links",
							"description": "Learn how you can use GraphQL queries with Apollo Client to load data from a server and display it in your React components."
						},
						"fields": {
							"slug": "/ember-apollo/2-queries-loading-links/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Mutations: Creating Links",
							"description": "Learn how you can use GraphQL mutations with Apollo Client. Use `ember-apollo-client` to send mutations."
						},
						"fields": {
							"slug": "/ember-apollo/3-mutations-creating-links/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Navigation",
							"description": "Learn how to use Ember components together with GraphQL and Apollo Client to implement navigation in an Ember app."
						},
						"fields": {
							"slug": "/ember-apollo/4-navigation/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Authentication",
							"description": "Learn best practices to implement authentication with GraphQL & Apollo Client to provide an email-and-password-based login in an Ember app with Graphcool."
						},
						"fields": {
							"slug": "/ember-apollo/5-authentication/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "More Mutations and Updating the Store",
							"description": "Learn how to use Apollo's imperative store API to update the cache after a GraphQL mutation. The updates will automatically end up in your Ember app."
						},
						"fields": {
							"slug": "/ember-apollo/6-more-mutations-and-updating-the-store/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Filtering: Searching the List of Links",
							"description": "Learn how to use filters with GraphQL and Apollo Client. Graphcool provides a powerful filter and ordering API that you'll explore in this example."
						},
						"fields": {
							"slug": "/ember-apollo/7-filtering-searching-the-list-of-links/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Summary",
							"description": "In this Fullstack GraphQL Tutorial with Ember & Apollo Client you learned how to build a production-ready Hackernews clone using ember-apollo-client & Graphcool."
						},
						"fields": {
							"slug": "/ember-apollo/8-summary/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Introduction",
							"description": "Learn how to build a Hackernews clone with GraphQL, React & Apollo Client. Use create-react-app for the frontend and graphql-yoga & Prisma for the backend."
						},
						"fields": {
							"slug": "/react-apollo/0-introduction/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Getting Started",
							"description": "Start building a Hackernews clone. Create the frontend with create-react-app and the backend with Prisma."
						},
						"fields": {
							"slug": "/react-apollo/1-getting-started/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Summary",
							"description": "In this Fullstack GraphQL Tutorial with React & Apollo Client you learned how to build a production-ready Hackernews clone using create-react-app & Prisma."
						},
						"fields": {
							"slug": "/react-apollo/10-summary/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Queries: Loading Links",
							"description": "Learn how you can use GraphQL queries with Apollo Client to load data from a server and display it in your React components."
						},
						"fields": {
							"slug": "/react-apollo/2-queries-loading-links/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Mutations: Creating Links",
							"description": "Learn how you can use GraphQL mutations with Apollo Client. Use Apollo's `<Mutation />` component to define and send mutations."
						},
						"fields": {
							"slug": "/react-apollo/3-mutations-creating-links/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Routing",
							"description": "Learn how to use react-router 4 together with GraphQL and Apollo Client to implement navigation in a React app. Each route will be represented as a `Link`."
						},
						"fields": {
							"slug": "/react-apollo/4-routing/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Authentication",
							"description": "Learn best practices to implement authentication with GraphQL & Apollo Client to provide an email-and-password-based login in a React app with Prisma."
						},
						"fields": {
							"slug": "/react-apollo/5-authentication/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "More Mutations and Updating the Store",
							"description": "Learn how to use Apollo's imperative store API to update the cache after a GraphQL mutation. The updates will automatically end up in your React components."
						},
						"fields": {
							"slug": "/react-apollo/6-more-mutations-and-updating-the-store/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Filtering: Searching the List of Links",
							"description": "Learn how to use filters with GraphQL and Apollo Client. Prisma provides a powerful filter and ordering API that you'll explore in this example."
						},
						"fields": {
							"slug": "/react-apollo/7-filtering-searching-the-list-of-links/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Realtime Updates with GraphQL Subscriptions",
							"description": "Learn how to implement realtime functionality using GraphQL subscriptions with Apollo Client & React. The websockets will be handled by subscriptions-transport-ws."
						},
						"fields": {
							"slug": "/react-apollo/8-subscriptions/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Pagination",
							"description": "Learn how to implement limit-offset pagination with GraphQL and Apollo Client in a React app. The Prisma API exposes the required arguments for lists."
						},
						"fields": {
							"slug": "/react-apollo/9-pagination/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Introduction",
							"description": "Learn how to build a production-ready Hackernews clone with GraphQL, React & Relay. You'll use create-react-app for the frontend and Graphcool for the backend."
						},
						"fields": {
							"slug": "/react-relay/0-introduction/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Getting Started",
							"description": "Start building a Hackernews clone. Create the frontend with create-react-app and the backend with Graphcool."
						},
						"fields": {
							"slug": "/react-relay/1-getting-started/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Queries: Loading Links",
							"description": "Learn how you can use GraphQL Fragments and Relay's QueryRenderer to load data from a server and display it in your React components."
						},
						"fields": {
							"slug": "/react-relay/2-queries-loading-links/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Routing",
							"description": null
						},
						"fields": {
							"slug": "/react-relay/4-routing/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Mutations: Creating Links",
							"description": "Learn how to use Relay Modern's new Mutation API to send mutations to a GraphQL server. With Relay Modern you can also update the cache directly after the mutation."
						},
						"fields": {
							"slug": "/react-relay/3-mutations-creating-links/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Authentication",
							"description": "Learn best practices to implement authentication with GraphQL & Relay to provide an email-and-password-based login in a React app with Graphcool."
						},
						"fields": {
							"slug": "/react-relay/5-authentication/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "More Mutations and Updating the Store",
							"description": "Learn how to use Relay Modern's imperative API to update the cache after a GraphQL mutation. The updates will automatically end up in your React components."
						},
						"fields": {
							"slug": "/react-relay/6-more-mutations-and-updating-the-store/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Realtime Updates with Subscriptions",
							"description": "Learn how to implement realtime functionality using GraphQL subscriptions with Relay Modern & React. The websockets will be handled by subscriptions-transport-ws."
						},
						"fields": {
							"slug": "/react-relay/7-subscriptions/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Summary",
							"description": "In this Fullstack GraphQL Tutorial with React & Relay Modern you learned how to build a production-ready Hackernews clone using create-react-app & Graphcool."
						},
						"fields": {
							"slug": "/react-relay/9-summary/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Pagination",
							"description": "Learn how to implement cursor-based pagination with GraphQL and Relay Modern using `PaginationContainer`. Relay uses the concept of connections to represent lists."
						},
						"fields": {
							"slug": "/react-relay/8-pagination/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Clients",
							"description": "Learn about GraphQL clients, like Relay and Apollo Client, and the features they provide, such as caching, schema validation and query/view colocation."
						},
						"fields": {
							"slug": "/advanced/0-clients/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Server",
							"description": "Learn about the major concepts of GraphQL servers, how it executes GraphQL queries with resolver functions and batches multiple requests."
						},
						"fields": {
							"slug": "/advanced/1-server/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "More GraphQL Concepts",
							"description": "Learn about advanced concepts of the GraphQL language like fragments, query arguments, aliases, interfaces and more SDL features"
						},
						"fields": {
							"slug": "/advanced/2-more-graphql-concepts/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Security",
							"description": "Learn about different security aspects and strategies for GraphQL, such as timeouts, maximum query depth, query complexity and throttling."
						},
						"fields": {
							"slug": "/advanced/4-security/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Common Questions",
							"description": "Answer common GraphQL questions about server-side caching, authentication, authorization, error handling, offline usage and more."
						},
						"fields": {
							"slug": "/advanced/5-common-questions/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Tooling and Ecosystem",
							"description": "Learn about the GraphQL ecosystem and its awesome tooling like the GraphiQL playgrounds or self-generating docs through introspection."
						},
						"fields": {
							"slug": "/advanced/3-tooling-and-ecosystem/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Introduction",
							"description": "Learn how to build a Hackernews clone with GraphQL, Angular & Apollo Client. You'll use angular-cli for the frontend and Graphcool for the backend."
						},
						"fields": {
							"slug": "/angular-apollo/0-introduction/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Getting Started",
							"description": "Start building a Hackernews clone. Create the frontend with angular-cli and the backend with Graphcool."
						},
						"fields": {
							"slug": "/angular-apollo/1-getting-started/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Deploying",
							"description": "Learn how to deploy an application built with Angular and Graphcool through Surge.sh."
						},
						"fields": {
							"slug": "/angular-apollo/10-deploying/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Summary",
							"description": "In this Fullstack GraphQL Tutorial with Angular & Apollo Client you learned how to build a production-ready Hackernews clone using angular-cli & Graphcool."
						},
						"fields": {
							"slug": "/angular-apollo/11-summary/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Queries: Loading link-items",
							"description": "Learn how you can use GraphQL queries with Apollo Client to load data from a server and display it in your Angular components."
						},
						"fields": {
							"slug": "/angular-apollo/2-queries-loading-links/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Mutations: Creating Links",
							"description": "Learn how you can use GraphQL mutations with Apollo Client. Use Apollo's service injected in the component to define and send mutations."
						},
						"fields": {
							"slug": "/angular-apollo/3-mutations-creating-links/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Routing",
							"description": "Learn how to use Angular Router  together with GraphQL and Apollo Client to implement navigation in an Angular app."
						},
						"fields": {
							"slug": "/angular-apollo/4-routing/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Authentication",
							"description": "Learn best practices to implement authentication with GraphQL & Apollo Client to provide an email-and-password-based login in an Angular app with Graphcool."
						},
						"fields": {
							"slug": "/angular-apollo/5-authentication/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Filtering: Searching the List of Links",
							"description": "Learn how to use filters with GraphQL and Apollo Client. Graphcool provides a powerful filter and ordering API that you'll explore in this example."
						},
						"fields": {
							"slug": "/angular-apollo/7-filtering-searching-the-list-of-links/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Realtime Updates with GraphQL Subscriptions",
							"description": "Learn how to implement realtime functionality using GraphQL subscriptions with Apollo Client & Angular. The websockets will be handled by subscriptions-transport-ws."
						},
						"fields": {
							"slug": "/angular-apollo/8-subscriptions/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "More Mutations and Updating the Store",
							"description": "Learn how to use Apollo's imperative store API to update the cache after a GraphQL mutation. The updates will automatically end up in your Angular components."
						},
						"fields": {
							"slug": "/angular-apollo/6-more-mutations-and-updating-the-store/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Introduction",
							"description": "Learn how to build a Hackernews clone with GraphQL, VueJS & Apollo Client. You'll use vue-cli for the frontend and Graphcool for the backend."
						},
						"fields": {
							"slug": "/vue-apollo/0-introduction/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Pagination",
							"description": "Learn how to implement limit-offset pagination with GraphQL and Apollo Client in a Angular app. The Graphcool API exposes the required arguments for lists."
						},
						"fields": {
							"slug": "/angular-apollo/9-pagination/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Deploying",
							"description": "Learn how to deploy an application built with VueJS and Graphcool through Surge.sh."
						},
						"fields": {
							"slug": "/vue-apollo/10-deploying/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Getting Started",
							"description": "Start building a Hackernews clone. Create the frontend with vue-cli and the backend with Graphcool."
						},
						"fields": {
							"slug": "/vue-apollo/1-getting-started/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Mutations: Creating Links",
							"description": "Learn how you can use GraphQL mutations with Apollo Client. Use Apollo's `mutate` method to define and send mutations."
						},
						"fields": {
							"slug": "/vue-apollo/3-mutations-creating-links/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Summary",
							"description": "In this Fullstack GraphQL Tutorial with VueJS & Apollo Client you learned how to build a production-ready Hackernews clone using vue-cli & Graphcool."
						},
						"fields": {
							"slug": "/vue-apollo/11-summary/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Queries: Loading Links",
							"description": "Learn how you can use GraphQL queries with Apollo Client to load data from a server and display it in your VueJS components."
						},
						"fields": {
							"slug": "/vue-apollo/2-queries-loading-links/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Filtering: Searching the List of Links",
							"description": "Learn how to use filters with GraphQL and Apollo Client. Graphcool provides a powerful filter and ordering API that you'll explore in this example."
						},
						"fields": {
							"slug": "/vue-apollo/7-filtering-searching-the-list-of-links/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Authentication",
							"description": "Learn best practices to implement authentication with GraphQL & Apollo Client to provide an email-and-password-based login in a VueJS app with Graphcool."
						},
						"fields": {
							"slug": "/vue-apollo/5-authentication/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Routing",
							"description": "Learn how to use vue-router together with GraphQL and Apollo Client to implement navigation in a VueJS app."
						},
						"fields": {
							"slug": "/vue-apollo/4-routing/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "More Mutations and Updating the Store",
							"description": "Learn how to use Apollo's imperative store API to update the cache after a GraphQL mutation. The updates will automatically end up in your VueJS components."
						},
						"fields": {
							"slug": "/vue-apollo/6-more-mutations-and-updating-the-store/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Realtime Updates with GraphQL Subscriptions",
							"description": "Learn how to implement realtime functionality using GraphQL subscriptions with Apollo Client & VueJS. The websockets will be handled by subscriptions-transport-ws."
						},
						"fields": {
							"slug": "/vue-apollo/8-subscriptions/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Pagination",
							"description": "Learn how to implement limit-offset pagination with GraphQL and Apollo Client in a VueJS app. The Graphcool API exposes the required arguments for lists."
						},
						"fields": {
							"slug": "/vue-apollo/9-pagination/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "GraphQL is the better REST",
							"description": "Learn why GraphQL is a more efficient and flexible alternative to REST APIs. It has a strong type system and avoids frontend issues like over- and underfetching."
						},
						"fields": {
							"slug": "/basics/1-graphql-is-the-better-rest/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Introduction",
							"description": "Learn all about GraphQL and why it is an API technology that's superior to REST. It is not only for React & Javascript developers but can be used for any API."
						},
						"fields": {
							"slug": "/basics/0-introduction/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Core Concepts",
							"description": "Learn about the basic GraphQL language concepts, such as Queries, Mutations, Subscriptions and the GraphQL Schema & SDL. Try them out in your own sandbox."
						},
						"fields": {
							"slug": "/basics/2-core-concepts/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Big Picture (Architecture)",
							"description": "Learn about different architectural use cases of GraphQL and the major components on the backend and the frontend, like resolver functions and client libraries."
						},
						"fields": {
							"slug": "/basics/3-big-picture/"
						}
					}
				}
			]
		}
	},
	"pathContext": {
		"slug": "/graphql-scala/0-introduction/"
	}
};


//////////////////
// WEBPACK FOOTER
// ./~/json-loader!./.cache/json/graphql-scala-0-introduction.json
// module id = ./node_modules/json-loader/index.js!./.cache/json/graphql-scala-0-introduction.json
// module chunks = 47